import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useKanban } from './KanbanContext';
import { useAuth } from './AuthContext';
import { LABS_API_URL, DEFAULT_HEADERS, getEmailDomainMark } from '../utils/config';
import { fetchUsersByBoardCode, fetchAdminUsers } from '../utils/api/permissionsApi';

function ReassignTaskModal({ task, onClose, onReassign }) {
  const { activeWorkspace, workspaceData } = useKanban();
  const { userData, currentUser } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [usersWithBoardAccess, setUsersWithBoardAccess] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usersLoaded, setUsersLoaded] = useState(false);
  
  // Estado para el dropdown con autocompletado
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [displaySuggestions, setDisplaySuggestions] = useState([]);
  const searchInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const modalRef = useRef(null);

  // Función para obtener el código del workspace activo
  const getActiveWorkspaceCode = useCallback(() => {
    if (!activeWorkspace || !workspaceData) return null;
    
    const currentWorkspace = workspaceData.find(ws => ws.id === activeWorkspace);
    
    if (currentWorkspace && currentWorkspace.code && currentWorkspace.code.trim() !== '') {
      return currentWorkspace.code;
    }
    
    if (userData && userData.body && userData.body.department) {
      const departmentMatch = userData.body.department.match(/^(\d+)/);
      return departmentMatch ? departmentMatch[1] : null;
    }
    
    return null;
  }, [activeWorkspace, workspaceData, userData]);

  // Filtrar usuarios por código del workspace
  const filterUsersByWorkspaceCode = useCallback((users, workspaceCode) => {
    if (!workspaceCode) {
      setFilteredUsers(users);
      return;
    }
    
    const filtered = users.filter(user => {
      if (!user.department) return false;
      
      if (user.department === workspaceCode || user.department?.toString() === workspaceCode?.toString()) {
        return true;
      }
      
      const userDeptMatch = user.department.match(/^(\d+)/);
      const userDeptCode = userDeptMatch ? userDeptMatch[1] : '';
      return userDeptCode === workspaceCode;
    });
    
    setFilteredUsers(filtered);
  }, []);

  // Formatear nombre de usuario desde email
  const formatUserName = (email) => {
    if (!email) return '';
    
    if (!email.includes('@')) return email;
    
    const namePart = email.split('@')[0];
    const nameWithSpaces = namePart.replace(/\./g, ' ');
    const nameParts = nameWithSpaces.split(' ');
    
    const firstName = nameParts[0] || '';
    const lastName = nameParts.length > 1 ? nameParts[1] : '';
    
    const formattedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
    const formattedLastName = lastName ? ' ' + lastName.charAt(0).toUpperCase() + lastName.slice(1).toLowerCase() : '';
    
    const mark = getEmailDomainMark(email);
    return formattedFirstName + formattedLastName + (mark ? ` ${mark}` : '');
  };

  // Cargar usuarios
  useEffect(() => {
    const fetchAllUsers = async () => {
      const cachedUsers = localStorage.getItem('allUsersData');
      const cachedTimestamp = localStorage.getItem('allUsersTimestamp');
      const currentTime = new Date().getTime();
      
      if (cachedUsers && cachedTimestamp && 
          (currentTime - parseInt(cachedTimestamp)) < 24 * 60 * 60 * 1000) {
        try {
          const users = JSON.parse(cachedUsers);
          setAllUsers(users);
          
          const workspaceCode = getActiveWorkspaceCode();
          if (workspaceCode) {
            filterUsersByWorkspaceCode(users, workspaceCode);
          } else {
            setFilteredUsers(users);
          }
          
          setUsersLoaded(true);
          return;
        } catch (error) {
          // Continuar con la petición a la API
        }
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${LABS_API_URL}/workspace/all-users`, {
          method: 'POST',
          headers: DEFAULT_HEADERS,
          body: JSON.stringify({})
        });
        
        if (!response.ok) {
          throw new Error(`Error al obtener usuarios: ${response.status}`);
        }
        
        const responseData = await response.json();
        let users = [];
        
        if (responseData && responseData.body) {
          try {
            users = JSON.parse(responseData.body);
            
            if (Array.isArray(users)) {
              setAllUsers(users);
              
              const workspaceCode = getActiveWorkspaceCode();
              if (workspaceCode) {
                filterUsersByWorkspaceCode(users, workspaceCode);
              } else {
                setFilteredUsers(users);
              }
              
              localStorage.setItem('allUsersData', JSON.stringify(users));
              localStorage.setItem('allUsersTimestamp', currentTime.toString());
            } else {
              setFilteredUsers([]);
            }
          } catch (parseError) {
            setError('Error al procesar los datos de usuarios');
            setFilteredUsers([]);
          }
        } else {
          setFilteredUsers([]);
        }
      } catch (err) {
        setError(err.message);
        setFilteredUsers([]);
      } finally {
        setLoading(false);
        setUsersLoaded(true);
      }
    };
    
    if (currentUser) {
      fetchAllUsers();
    }
  }, [currentUser, getActiveWorkspaceCode, filterUsersByWorkspaceCode]);

  // Obtener usuarios con acceso especial al tablero
  useEffect(() => {
    const fetchUsersWithBoardAccess = async () => {
      const workspaceCode = getActiveWorkspaceCode();
      if (!workspaceCode) {
        setUsersWithBoardAccess([]);
        return;
      }

      try {
        const usersWithAccess = await fetchUsersByBoardCode(workspaceCode);
        setUsersWithBoardAccess(usersWithAccess);
      } catch (error) {
        setUsersWithBoardAccess([]);
      }
    };

    if (usersLoaded) {
      fetchUsersWithBoardAccess();
    }
  }, [activeWorkspace, workspaceData, usersLoaded, getActiveWorkspaceCode]);

  // Obtener usuarios administradores
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const adminUserEmails = await fetchAdminUsers();
        setAdminUsers(adminUserEmails);
      } catch (error) {
        console.error('Error fetching admin users:', error);
        setAdminUsers([]);
      }
    };

    if (usersLoaded) {
      fetchAdmins();
    }
  }, [usersLoaded]);

  // Filtrar usuarios basado en la búsqueda
  useEffect(() => {
    const adminUsersList = adminUsers
      .filter(email => !filteredUsers.some(u => u.email?.toLowerCase() === email?.toLowerCase()))
      .map(email => {
        const fullUser = allUsers.find(u => u.email?.toLowerCase() === email?.toLowerCase());
        const hasAccess = usersWithBoardAccess.some(u => u?.toLowerCase() === email?.toLowerCase());
        if (fullUser) {
          return {
            ...fullUser,
            isAdmin: true,
            hasSpecialAccess: hasAccess
          };
        }
        return {
          email: email,
          department: 'Administrador',
          isAdmin: true,
          hasSpecialAccess: hasAccess
        };
      });
    
    const additionalUsers = usersWithBoardAccess
      .filter(email => !filteredUsers.some(u => u.email === email) && !adminUsers.includes(email))
      .map(email => ({
        email: email,
        department: 'Acceso especial',
        hasSpecialAccess: true,
        isAdmin: false
      }));
    
    const combinedUsers = [
      ...filteredUsers.map(user => ({
        ...user,
        hasSpecialAccess: usersWithBoardAccess.some(u => u?.toLowerCase() === user.email?.toLowerCase()),
        isAdmin: adminUsers.some(a => a?.toLowerCase() === user.email?.toLowerCase())
      })),
      ...adminUsersList,
      ...additionalUsers
    ];
    
    // Ordenar: administradores primero, luego el resto
    combinedUsers.sort((a, b) => {
      if (a.isAdmin && !b.isAdmin) return -1;
      if (!a.isAdmin && b.isAdmin) return 1;
      return 0;
    });
    
    if (searchQuery.trim() === '') {
      setDisplaySuggestions(combinedUsers);
    } else {
      const filtered = combinedUsers.filter(user => {
        const userName = formatUserName(user.email).toLowerCase();
        return userName.includes(searchQuery.toLowerCase());
      });
      setDisplaySuggestions(filtered);
    }
  }, [searchQuery, filteredUsers, usersWithBoardAccess, adminUsers, allUsers]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        modalRef.current && 
        !modalRef.current.contains(event.target) &&
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        searchInputRef.current && 
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Manejar el cambio en el input de búsqueda
  const handleFilterChange = (e) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    setShowSuggestions(true);
  };

  // Manejar la selección de un usuario
  const handleSelectUser = (user) => {
    if (onReassign) {
      onReassign(user.email);
    }
    onClose();
  };

  // Obtener iniciales del usuario
  const getUserInitials = (email) => {
    if (!email) return '?';
    const formatted = formatUserName(email);
    const parts = formatted.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return formatted[0]?.toUpperCase() || '?';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="reassign-modal" onClick={(e) => e.stopPropagation()} ref={modalRef}>
        <div className="reassign-modal-header">
          <h3>Reasignar tarea</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="reassign-modal-content">
          <div className="reassign-search-container">
            <input
              type="text"
              className="reassign-search-input"
              placeholder="Buscar responsable..."
              value={searchQuery}
              onChange={handleFilterChange}
              onFocus={() => setShowSuggestions(true)}
              ref={searchInputRef}
            />
            
            {showSuggestions && displaySuggestions.length > 0 && (
              <div className="reassign-suggestions" ref={suggestionsRef}>
                {displaySuggestions.map((user, index) => {
                  const formattedName = formatUserName(user.email);
                  const isCurrentAssignee = task.assignee === user.email;
                  
                  return (
                    <div 
                      key={index} 
                      className={`reassign-suggestion-item ${isCurrentAssignee ? 'current-assignee' : ''}`}
                      onClick={() => handleSelectUser(user)}
                    >
                      <div className="user-avatar-small" data-initial={getUserInitials(user.email).charAt(0)}>
                        {getUserInitials(user.email)}
                      </div>
                      <div className="user-info">
                        <span className="user-name">{formattedName}</span>
                        {isCurrentAssignee && (
                          <span className="current-badge">Responsable actual</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {loading && (
            <div className="loading-message">
              <div className="mini-spinner"></div>
              <span>Cargando usuarios...</span>
            </div>
          )}
          
          {error && (
            <div className="error-message">
              Error: {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReassignTaskModal;
